import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Box, Step, StepButton, StepLabel, Stepper, Typography } from '@material-ui/core';
import SwiperCore, { Pagination } from 'swiper/core';
import 'swiper/components/pagination/pagination.min.css';

import './programBlock.scss';
import { RowBlock } from '../MainPage';
import Button, { ContainButton, RightArrowButton } from '../../../components/buttons/Buttons';
import { PlayIcon, StepperIcon } from '../../../components/Icons';
import { ClassName } from '../../../components/types';
import { useIsMobile, useIsSDesktop, usePageMargin } from '../../../components/layout/responsive';
import { Swiper, SwiperSlide } from '../../../components/elements/Swiper';
import { PROGRAM_PAGE } from '../../../routes';
import { Program, ProgramSection, StringOrDate } from '../../../services/models';
import { timeRangeFormat, useMoment } from '../../../hooks/moment';
import {
  getAllSectionSpeakers,
  isCoffeeBreak,
  prepareSectionEvent,
  SectionEvent,
  SpeakersRow,
} from '../../program/components';
import ProgramSectionDetailed, { Block } from '../../program/detailed/ProgramSectionDetailed';
import { useElementAnimatedHeight } from '../../../hooks/react';


SwiperCore.use([Pagination]);

type DateSection = { name: string, items: ProgramSection[] };

export default function ProgramBlock({ className, program }: ClassName & { program: Program }) {
  const moment = useMoment();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isSDesktop = useIsSDesktop(-300);
  const pageMargin = usePageMargin();
  const [selectedDate, setSelectedDate] = useState<{ date: StringOrDate, sections: ProgramSection[] } | undefined>(undefined);
  const [sections, setSections] = useState<DateSection[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<DateSection>();
  const [detailedItem, setDetailedItem] = useState<SectionEvent>();
  const { ref: containerRef, style: containerStyle } = useElementAnimatedHeight<HTMLDivElement>([selectedLocation]);
  const dates = program.dates ?? [];
  const selectedSections = selectedLocation?.items;
  const openDetailed = !!detailedItem;
  const handleSectionClick = useCallback((item: ProgramSection) => {
    setDetailedItem(prepareSectionEvent(item, program.year));
  }, [program.year]);
  const handleDetailedClose = useCallback(() => {
    setDetailedItem(undefined);
  }, []);

  useEffect(() => {
    if (!Boolean(dates.length)) return;
    setSelectedDate(dates[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.dates]);
  useEffect(() => {
    const item: { [key: string]: ProgramSection[] } = (selectedDate?.sections ?? []).reduce((r, i) => {
      const items = r[i.location] ?? [];
      items.push(i);
      r[i.location] = items;
      return r;
    }, {} as any);
    const items = Object.entries(item).map(([k, v]) => ({ name: k, items: v })).filter(i => i.name);
    setSections(items);
    setSelectedLocation(items[0]);
  }, [selectedDate]);

  const programBtn = <RightArrowButton href={PROGRAM_PAGE}>{t('main.all_program')}</RightArrowButton>;
  return (
    <RowBlock
      className={classnames('MainProgramBlock', className)} title={t('main.forum_program_year', { year: program.year })}
      action={<Box className={'actions'} display="flex" flexDirection="column" alignItems="flex-end">
        {program.stream_link && (
          <ContainButton endIcon={<PlayIcon />} href={program.stream_link} target="_blank"
                         referrerPolicy="no-referrer" rel="noreferrer">
            {t('program.watch_translation')}
          </ContainButton>
        )}
        {isSDesktop && !isMobile && (<div style={{ marginTop: 24 }}>{programBtn}</div>)}
      </Box>}
    >
      <div ref={containerRef} style={{ ...containerStyle, marginBottom: -30, paddingBottom: 30 }}>
        {isMobile && (<div style={{ marginTop: 24, marginLeft: pageMargin }}>{programBtn}</div>)}
        <Box className={'MainProgramBlock__dates'} display="flex" justifyContent="space-between">
          <Swiper
            className={'Swiper'}
            watchSlidesProgress={true} watchSlidesVisibility={true} slidesPerView="auto"
            slidesOffsetBefore={pageMargin}
            slidesOffsetAfter={pageMargin}
            touchStartPreventDefault={false}
          >
            {dates.map((i) => {
              const selected = selectedDate === i;
              return (
                <SwiperSlide key={`${i.date}`} className={classnames('MainProgramBlock__dates__date', 'swiperSlide')}>
                  <Button
                    className={classnames('MainProgramBlock__dates__dateBtn', { selected })}
                    variant="outlined" color={selected ? 'inherit' : 'primary'}
                    onClick={() => setSelectedDate(i)}
                  >
                    {moment(i.date).format('D MMMM')}
                  </Button>
                </SwiperSlide>
              );
            })}
          </Swiper>
          {!isSDesktop && (<div style={{ marginLeft: 12, display: 'flex', alignItems: 'center' }}>{programBtn}</div>)}
        </Box>
        <Box className={'MainProgramBlock__program'} display="flex" justifyContent="space-between">
          <Box className={'program__sections'}>
            <SectionSlider key={`${selectedDate?.date}`} sections={sections} onChange={setSelectedLocation} />
          </Box>
          <Box className={'section__content'} flex={1} display="flex" flexDirection="column">
            {!isSDesktop && (selectedSections?.map((i, ind) => (
              <ProgramSectionCard key={ind} item={i} onClick={handleSectionClick} />
            )))}
            {isSDesktop && (
              <Swiper
                key={`${selectedDate?.date}_${selectedLocation?.name}`}
                className={'Swiper'}
                pagination={{ clickable: true }}
                watchSlidesProgress={true} watchSlidesVisibility={true} slidesPerView="auto"
                slidesOffsetBefore={pageMargin}
                slidesOffsetAfter={pageMargin}
                spaceBetween={isMobile ? 20 : 24}
                touchStartPreventDefault={false}
              >
                {selectedSections?.map((i, ind) => (
                  <SwiperSlide key={ind} className={'swiperSlide'}>
                    <ProgramSectionCard item={i} onClick={handleSectionClick} />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}

            {/*<Box className={'section__content__wrapper'} display="flex" flexDirection="column">*/}
            {/*  {theme && (*/}
            {/*    <Box className={'section__content__theme'}>*/}
            {/*      /!*<Typography color="primary">{t('main.program_section_theme')}</Typography>*!/*/}
            {/*      <Typography className={'value'} variant="subtitle1" color="secondary">{theme}</Typography>*/}
            {/*    </Box>*/}
            {/*  )}*/}
            {/*  {showSection && (*/}
            {/*    <>*/}
            {/*      <Box className={'section__content__timeBlock'} display="flex" alignItems="center">*/}
            {/*        <Typography className={'time'}*/}
            {/*                    variant="inherit">{timeRangeFormat(section!.start_at, section!.finish_at)}</Typography>*/}
            {/*        <Typography className={'location'} variant="inherit">{section!.location}</Typography>*/}
            {/*      </Box>*/}
            {/*      {Boolean(section?.speakers?.length) && (*/}
            {/*        <Block className={'section__content__speakers'} title={t('program_detailed.speakers')}>*/}
            {/*          {section?.speakers?.map(i => (*/}
            {/*            <SpeakersRow className={'speaker'} key={i.id} speakers={[i]} clickable showOrganization />))}*/}
            {/*        </Block>*/}
            {/*      )}*/}
            {/*    </>*/}
            {/*  )}*/}
            {/*</Box>*/}
            {/*{!isSDesktop && hasSpeeches && (section?.speeches?.map((i, ind) => (*/}
            {/*  <ProgramSpeechCard key={ind} item={i} />*/}
            {/*)))}*/}
            {/*{isSDesktop && hasSpeeches && (*/}
            {/*  <Swiper*/}
            {/*    className={'Swiper'}*/}
            {/*    pagination={{ clickable: true }}*/}
            {/*    watchSlidesProgress={true} watchSlidesVisibility={true} slidesPerView="auto"*/}
            {/*    slidesOffsetBefore={pageMargin}*/}
            {/*    slidesOffsetAfter={pageMargin}*/}
            {/*    spaceBetween={isMobile ? 20 : 24}*/}
            {/*    touchStartPreventDefault={false}*/}
            {/*  >*/}
            {/*    {section?.speeches?.map((i, ind) => (*/}
            {/*      <SwiperSlide key={ind} className={'swiperSlide'}>*/}
            {/*        <ProgramSpeechCard item={i} />*/}
            {/*      </SwiperSlide>*/}
            {/*    ))}*/}
            {/*  </Swiper>*/}
            {/*)}*/}
          </Box>
        </Box>
      </div>

      <ProgramSectionDetailed open={openDetailed} onClose={handleDetailedClose} section={detailedItem} />
    </RowBlock>
  );
}

function ProgramSectionCard({ className, item, onClick }: ClassName & {
  item: ProgramSection, onClick: (item: ProgramSection) => void,
}) {
  const { t } = useTranslation();
  const coffeeBreak = isCoffeeBreak(item);
  const handleClick = useCallback(() => {
    if (coffeeBreak) return;
    onClick(item);
  }, [item, coffeeBreak, onClick]);
  const time = timeRangeFormat(item.start_at, item.finish_at);
  const speakers = getAllSectionSpeakers(item);
  return (
    <div className={classnames('ProgramCard', { clickable: !coffeeBreak }, className)} onClick={handleClick}>
      <Typography className={'ProgramCard__time'} color="primary">{time}</Typography>
      <Typography className={'ProgramCard__title'} variant="subtitle2">{item.subject}</Typography>
      {Boolean(speakers?.length) && (
        <Block
          className={'section__content__speakers'}
          title={speakers.length > 1 ? t('program_detailed.speakers') : null}
        >
          {speakers?.map(i => (
            <div key={i.id} onClick={(e) => e.stopPropagation()}>
              <SpeakersRow className={'speaker'} speakers={[i]} clickable showOrganization />
            </div>
          ))}
        </Block>
      )}
      {/*<SpeakersRow className={'SpeakerCard'} speakers={speakers} showOrganization />*/}
    </div>
  );
}

// function ProgramSpeechCard({ className, item }: ClassName & { item: ProgramSpeech }) {
//   const time = timeRangeFormat(item.start_at, item.finish_at);
//   return (
//     <Box className={classnames('ProgramCard', className)}>
//       <Typography className={'ProgramCard__time'} color="primary">{time}</Typography>
//       <Typography className={'ProgramCard__title'} variant="subtitle2">{item.subject}</Typography>
//       <SpeakersRow className={'SpeakerCard'} speakers={item.speakers} showOrganization />
//     </Box>
//   );
// }

function SectionSlider({ sections, value, onChange }: {
  sections: DateSection[], value?: DateSection, onChange: (value: DateSection) => void
}) {
  const isMobile = useIsMobile();
  const isSDesktop = useIsSDesktop(-300);
  const [activeStep, setActiveStep] = React.useState(0);

  const handleStep = (step: number) => () => {
    setActiveStep(step);
    onChange(sections[step]);
  };
  useEffect(() => {
    const step = value ? sections.findIndex(i => i === value) : 0;
    if (step !== activeStep) setActiveStep(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, value]);
  const children = (
    <Stepper
      className={'ProgramStepper'} nonLinear alternativeLabel
      orientation={isSDesktop ? 'horizontal' : 'vertical'} activeStep={activeStep}
    >
      {sections.map((i, ind) => (
        <Step key={ind} className={'ProgramStepper__step'}>
          <StepLabel StepIconComponent={StepperIcon}>
            <StepButton orientation={isSDesktop ? 'horizontal' : 'vertical'} alternativeLabel={false}
                        onClick={handleStep(ind)}>
              <Typography
                className={classnames('ProgramStepper__label', { active: ind === activeStep })}
                variant="subtitle1" color="inherit">{i.name}</Typography>
            </StepButton>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
  if (!isSDesktop || (!isMobile && sections.length < 7)) return children;
  return (
    <Swiper
      className={'Swiper'}
      freeMode={true}
      slidesPerView="auto"
      // slidesOffsetBefore={pageMargin}
      // slidesOffsetAfter={pageMargin}
      touchStartPreventDefault={false}
    >
      <SwiperSlide className={'swiperSlide'}>
        {children}
      </SwiperSlide>
    </Swiper>
  );
}
